import React, { useContext } from 'react';
import Table, {
  MidTitle,
  HeaderRow,
  TableRow
} from '../../common/Widgets/Table';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SiteContext } from '../../../context/SiteContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import Links, { LinkItem } from '../../common/Widgets/Links';
import { PageAbsolutePath } from '../../../consts/Common';

interface Props {
  isShowingInPopup?: boolean;
}

const FairUseNoticePage = ({
  isShowingInPopup = false
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const { brandDisplayName, brandDisplayFullName, brandPhoneNumbers } = brand;

  const phoneNumber = brandPhoneNumbers.changesAndComplaintsNumber;

  const getBackLink = (): LinkItem[] => {
    if (locationState?.navigateToLanding === true) {
      if (site.user?.isDealer) {
        return [
          { to: PageAbsolutePath.Insurance.DealerLandingPage, text: 'Back' }
        ];
      } else {
        return [{ to: PageAbsolutePath.Insurance.Landing, text: 'Back' }];
      }
    } else {
      return [{ to: '', text: 'Back', onClick: goBack }];
    }
  };

  const goBack = (): void => {
    if (locationState?.navigateToLanding === true) {
      if (site.user?.isDealer) {
        navigate(PageAbsolutePath.Insurance.DealerLandingPage);
      } else {
        navigate(PageAbsolutePath.Insurance.Landing);
      }
    } else {
      navigate(-1);
    }
  };

  const insurerTableHeader: HeaderRow = {
    columns: [
      'Insurer/Product Provider Name:',
      'Electronic Version can be obtained here:',
      'Contact details to request a copy of their Fair Use Notice:'
    ]
  };

  const freeDriveawayCover: MidTitle = {
    title: 'Free Driveaway Cover',
    colspan: 3
  };
  const optionalProducts: MidTitle = { title: 'Optional Products', colspan: 3 };
  const premiumFinance: MidTitle = { title: 'Premium Finance', colspan: 3 };

  const insurerTableRows: TableRow[] = [
    {
      columns: [
        'Allianz ABC Insurance (formerly LV ABC Insurance)',
        <a
          className='in-text'
          href='https://www.allianz.co.uk/broker-customer/privacy-notice.html'
          target='_blank'>
          https://www.allianz.co.uk/broker-customer/privacy-notice.html
        </a>,
        'Write to: Data Protection Officer, Allianz, 57 Ladymead, Guildford, Surrey,GU1 1DB'
      ]
    },
    {
      columns: [
        'Allianz Highway Insurance (formerly LV Highway Insurance)',
        <a
          className='in-text'
          href='https://www.allianz.co.uk/broker-customer/privacy-notice.html'
          target='_blank'>
          https://www.allianz.co.uk/broker-customer/privacy-notice.html
        </a>,

        'Write to: Data Protection Officer, Allianz, 57 Ladymead, Guildford, Surrey,GU1 1DB'
      ]
    },
    {
      columns: [
        'Ageas',
        <a
          className='in-text'
          href='https://www.ageas.co.uk/privacy-policies/customers-privacy-policy/'
          target='_blank'>
          https://www.ageas.co.uk/privacy-policies/customers-privacy-policy/
        </a>,
        'Write to: Data Protection Officer, Ageas House, Hampshire Corporate Park, Templars Way, Eastleigh, Hampshire, SO53 3YA.'
      ]
    },
    {
      columns: [
        'Axa',
        <a
          className='in-text'
          href='https://www.axa.co.uk/privacy-policy/'
          target='_blank'>
          https://www.axa.co.uk/privacy-policy/
        </a>,
        'Write to: Data Protection Officer AXA Insurance UK Plc Brooke Lawrance House, 80 Civic Drive Ipswich, Suffolk IP1 2AN'
      ]
    },
    {
      columns: [
        'Covea',
        <a
          className='in-text'
          href='https://www.coveainsurance.co.uk/privacy-notices/motor-insurance-section/'
          target='_blank'>
          https://www.coveainsurance.co.uk/privacy-notices/motor-insurance-section/
        </a>,
        'Write to: The Data Protection Officer, Covea Insurance, Norman Place, Reading, RG1 8DA'
      ]
    },
    { columns: [freeDriveawayCover] },
    {
      columns: [
        'KGM',
        <a
          className='in-text'
          href='https://www.kgmus.co.uk/privacy.html'
          target='_blank'>
          https://www.kgmus.co.uk/privacy.html
        </a>,
        'Write to: Data Protection Office, KGMUS, 2 des Roches Square, Witney OX28 4LE.'
      ]
    },
    { columns: [optionalProducts] },
    {
      columns: [
        'AmTrust Europe Limited - Motor Legal Expenses Insurance (From 1 January 2023), Courtesy Car Plus - 21 days (From 1 January 2023 to 23 March 2024)',
        <a
          className='in-text'
          href='https://www.arclegal.co.uk/privacy-policy/'
          target='_blank'>
          https://www.arclegal.co.uk/privacy-policy/
        </a>,
        'Write to: The Data Protection Officer, Arc Legal Assistance Ltd, The Gatehouse, Lodge Park, Lodge Lane, Colchester CO4 5NE'
      ]
    },
    {
      columns: [
        'HDI Global Speciality SE (administered by ARAG Plc) – Courtesy Car Plus 28 days (From 12 May 2023)',
        <a
          className='in-text'
          href='https://www.hdi-specialty.com/int/en/legals/privacy'
          target='_blank'>
          https://www.hdi-specialty.com/int/en/legals/privacy
        </a>,
        'Write to: Data Protection Officer, ARAG plc, 9 Whiteladies Road, Clifton, Bristol, BS8 1NN'
      ]
    },
    {
      columns: [
        'Fortegra Europe Insurance Company Limited - Key Protection Insurance, Excess Insurance (From 12 May 2023)',
        <a
          className='in-text'
          href='https://www.fortegra.eu/privacy-policy/'
          target='_blank'>
          https://www.fortegra.eu/privacy-policy/
        </a>,
        'Write to: The Data Protection Officer, Fortegra Europe Insurance Company Limited, Office 13, SOHO Office The Strand, Fawwara Building, Triq l-Imsida, Gzira, GZR 1401, Malta or email dpofficer@fortegramalta.com.'
      ]
    },
    {
      columns: [
        'AWP P&C SA (part of Allianz Global Assistance) - Breakdown Assistance',
        <a
          className='in-text'
          href='https://www.allianz-assistance.co.uk/privacy-notice.html#'
          target='_blank'>
          https://www.allianz-assistance.co.uk/privacy-notice.html#
        </a>,
        <Link className='in-text' to='#'>
          AzPUKDP@allianz.com
        </Link>
      ]
    },
    { columns: [premiumFinance] },
    {
      columns: [
        'Premium Credit Limited',
        <a
          className='in-text'
          href='https://www.premiumcredit.com/privacy-notice'
          target='_blank'>
          https://www.premiumcredit.com/privacy-notice
        </a>,
        'Write to: Data Protection Officer, Premium Credit Limited, Ermyn House, Ermyn Way, Leatherhead, Surrey, KT22 8UX or email: DataProtectionOfficer@pcl.co.uk'
      ]
    }
  ];

  const claimsTableHeader: HeaderRow = {
    columns: [
      'Claims Partner:',
      'Electronic Version can be obtained here:',
      'Contact details to request a copy of their Fair Use Notice:'
    ]
  };
  const claimsTableRows: TableRow[] = [
    {
      columns: [
        'Kindertons',
        <a
          className='in-text'
          href='https://www.kindertons.co.uk/'
          target='_blank'>
          https://www.kindertons.co.uk/ (select the Group Privacy Policy
          document)
        </a>,
        'Write to: Data Protection Officer, Kindertons House, Marshfield Bank, Crewe, Cheshire, CW2 8UY'
      ]
    }
  ];

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  return (
    <div className='fair-use-notice'>
      {!isShowingInPopup && (
        <Links
          id='back-link'
          title=''
          items={getBackLink()}
          icon={chevronLeftComponent}
        />
      )}
      <h2>About this Fair Use Notice</h2>
      <h4>
        This Fair Use Notice relates to {brandDisplayFullName} Insurance and{' '}
        {brandDisplayName} Accident Aftercare services.
      </h4>
      <p>
        This Fair Use Notice is to help you understand how we will collect,
        process and share your personal data. This allows you to make an
        informed decision as to whether you want to provide us with your data
        and if you want to give us additional permissions relating to it.
      </p>
      <p>
        It also sets out your rights in relation to this data and how to
        complain to the Information Commissioner if you are unsatisfied with how
        we have handled your personal data.
      </p>
      <p>
        You can update the consents you have given us{' '}
        <Link
          className='in-text'
          id='lnkUpdatePreference'
          to={PageAbsolutePath.Insurance.UpdatePreferencesPage}>
          here
        </Link>
      </p>
      <h4>Who we are.</h4>
      <p>
        We, in this policy refers to Car Care Plan Limited. It may be that we
        provide you with one or more of the following:.
      </p>
      <ul>
        <li>
          arranging {brandDisplayFullName} Insurance and related ancillary
          products
        </li>
        <li>
          Claims management in respect of {brandDisplayFullName} Insurance or
          and {brandDisplayName} Accident Aftercare services. such as Call{' '}
          {brandDisplayName} First.
        </li>
        <li>An Excess Return Voucher or Free Driveaway Insurance policy.</li>
        <li>
          Arranging credit on your behalf with our partnered lender for the
          purposes of paying for your {brandDisplayFullName} Insurance monthly
          (Premium Finance).
        </li>
      </ul>
      <p>
        Car Care Plan Limited is the data controller in relation to the data you
        provide to us.
      </p>
      <p>
        Car Care Plan Limited is a specialist company appointed by{' '}
        {brandDisplayName} to ensure its customers are able to obtain insurance
        policies and use accident claims services which guarantee to repair your
        vehicle at a {brandDisplayName} approved repairer using only genuine{' '}
        {brandDisplayName} parts. For more information about Car Care Plan visit
        <a
          className='in-text'
          href='https://www.carcareplan.com'
          target='_blank'>
          www.carcareplan.com
        </a>
      </p>
      <p>
        Car Care Plan Limited has appointed a Data Protection Officer who can be
        contacted at:
        <br />
        <br />
        <Link className='in-text' to='#'>
          CCPH_DPA@carcareplan.co.uk
        </Link>
      </p>
      <p>
        The Data Protection Officer, Car Care Plan Limited, Jubilee House, 5 Mid
        Point Business Park, Thornbury, West Yorkshire, BD3 7AG.
      </p>
      <p>
        <u>Other Joint Data Controllers</u>
      </p>
      <p>
        For some parts of our service we are also Joint Data Controllers with
        other Parties. Where this is the case we remain primarily responsible
        for facilitating your rights as a data subject.
      </p>
      <p>
        When we get an insurance quote from our specially selected panel of
        insurers, then we and the insurer are joint data controllers. Where you
        opt to pay for your insurance monthly, then we and the lender providing
        the finance are joint data controllers.
      </p>
      <p>
        For more information about how our insurer panel use your personal data,
        you can find a copy of the respective Fair Processing Notice as follows:
      </p>
      <Table
        id='fair-processing-notice'
        header={insurerTableHeader}
        rows={insurerTableRows}
      />
      <p>
        <b>Please note:</b> If the insurer or product provider is not listed
        above, please{' '}
        <Link className='in-text' to={PageAbsolutePath.Insurance.HelpPage}>
          contact us.
        </Link>
      </p>
      <p>
        When you make a claim under an insurance policy or use our Accident
        Aftercare Service and that service involves the provision of a hire car
        or you ask for assistance with a personal injury claim then for that
        part of the process, we will be joint data controllers with our claims
        handling partner,{' '}
        <a
          className='in-text'
          href='https://www.kindertons.co.uk'
          target='_blank'>
          Kindertons Ltd
        </a>
      </p>
      <Table
        id='claims-handling-partner'
        header={claimsTableHeader}
        rows={claimsTableRows}
      />
      <h4>Who you are.</h4>
      <p>
        You are anyone whose personal information we collect including anyone:
      </p>
      <ul>
        <li>
          requesting an insurance quote, for any of the insurance products we
          provide, or free driveaway insurance;
        </li>
        <li>
          any Driveaway Insurance holder, policyholder and any named driver;
        </li>
        <li>
          any person issued with an Excess Return or Excess Protect Voucher
        </li>
        <li>
          any person requesting a vehicle repair be managed by us either under
          an insurance policy we have issued or as part of an accident aftercare
          service;
        </li>
        <li>
          any other party involved in an insurance claim or vehicle repair; or
        </li>
        <li>
          any person you authorise to act on your behalf in dealing with any of
          the services that we provide.
        </li>
      </ul>
      <p>
        When requesting an insurance quote, you should only provide details of
        named drivers if you have their permission to do so, including any
        sensitive personal data, and you should make them aware of this Fair Use
        Notice. By providing their details you are confirming to us that that is
        the case.
      </p>
      <h4>Data we collect about you.</h4>
      <p>
        <u>Data You Provide</u>
      </p>
      <p>
        We collect the data that you give us when we provide you with a quote or
        when you make an insurance claim or use an accident aftercare service
        and any other time that we speak to you or you contact us.
      </p>
      <p>
        The data we collect from you for an insurance quote and in the handling
        of any claims may include sensitive personal data relating to any
        convictions you have or that are pending and details of certain medical
        conditions.
      </p>
      <p>
        We record telephone calls with you and these call recordings also form
        part of your personal data.
      </p>
      <p>
        We also store information about your contacts with us such as the time,
        length and source of calls, the IP address that you use to access our
        website, whether you have read or replied to an email we send you; all
        this data helps us improve our service, detect fraud and resolve
        complaints when they arise. Our websites may use cookies (small text
        files) to track and record your activity and to allow us to personalise
        our websites and enhance your experience of them.
      </p>
      <p>
        On occasions, you may ask your dealer to provide us with all or some of
        this data to help you set up a quote, free driveaway insurance or
        policy. Where you ask the dealer to do this we treat this as data that
        you have provided to us and we consider that the dealer has been acting
        on your behalf in providing us that data.
      </p>
      <p>
        <u>Data We Collect from Others</u>
      </p>
      <p>
        We may have made contact with you initially having been provided with
        your details by a third party, such as your finance provider, following
        an application by you for vehicle finance. When this is the case, they
        may provide us with data that they hold which is relevant for the
        purpose of us providing you with an insurance quote. This is in order to
        minimise the need for you to provide data to us. We will only receive
        this data where we are satisfied that the provider has your consent or
        another lawful reason to provide it to us and we will tell you when we
        contact you who provided us with the data.
      </p>
      <p>
        As part of providing an insurance quote and administrating a policy
        including handling a claim we or the insurers and product providers we
        use may collect data from third parties about you including
      </p>
      <ul>
        <li>
          your vehicle manufacturer who may provide details of cars they and
          their dealers have sold.
        </li>
        <li>
          suppliers who provide services to you as part of our service such as
          repairers and roadside recovery agents
        </li>
        <li>other parties to an accident and their insurers</li>
        <li>the Motor Insurance Database (MID);</li>
        <li>the Claims and Underwriting Exchange (CUE);</li>
        <li>the Motor Insurance Anti-Fraud and Theft Register (MIAFTR);</li>
        <li>
          the DVLA and MyLicence, a database that allows us to verify your
          driving licence details;
        </li>
        <li>credit reference and identity verification agencies;</li>
        <li>
          Lexis-Nexis NCD Database, a database that allows us to verify your No
          Claims Discount;
        </li>
        <li>emergency services, police, medical and legal practices;</li>
        <li>
          If you have a usage-based product or add-on, then we will receive the
          data from your vehicle’s telematics provider that provides us with the
          relevant usage data for that product or add-on.
        </li>
      </ul>
      <h4>How we use your data.</h4>
      <p>
        We will use your data to fulfil requests you make of us and to meet the
        requirements of any contract we enter with you or any promotion we
        provide to you.
      </p>
      <p>For instance</p>
      <ul>
        <li>
          if you ask us for an insurance quote we will process your data to
          provide that for you including providing your details to one or more
          insurers;
        </li>
        <li>
          if you have an insurance policy with us we will process that data to
          fulfil our obligations under that contract;
        </li>
        <li>
          If you ask us to repair your car we will appoint a repairer and
          provide them with you and your cars details so that they can carry out
          that repair and pass back to us information about that repair.
        </li>
        <li>
          If we provide you with an Excess Return or Excess Protect Voucher we
          will keep a record that you have been issued with it and process your
          data in connection with it.
        </li>
        <li>
          If you have a usage-based product or add-on then in addition, we and
          our underwriters will use the data collected and processed by the
          relevant telematics partner to
        </li>
        <ul>
          <li>
            Understand, as relevant to the applicable product or add-on, your
            vehicle usage and to use this information to fulfil the usage based
            features of the product;
          </li>
        </ul>
      </ul>
      <p>
        Additionally, we will process your data to fulfil our legitimate
        interests where they are not overridden by your interests. This
        includes:
      </p>
      <ul>
        <li>
          If you have asked us for a quote or received a product or service from
          us, then we may contact you to let you know of similar products and
          services that we provide from time to time. You can opt out of this at
          any time{' '}
          <Link
            className='in-text'
            id='lnkUpdatePreference'
            to={PageAbsolutePath.Insurance.UpdatePreferencesPage}>
            here
          </Link>{' '}
          and any such communication will let you know how to do that easily.
        </li>
        <li>
          Inviting renewal of your policies or Excess Return or Excess Protect
          Voucher;
        </li>
        <li>
          Contacting you to assist you when you are accessing our services
          online;
        </li>
        <li>
          Letting you know when a Driveaway Insurance policy is going to expire
          and providing you with a means to take up a full motor insurance
          policy;
        </li>
        <li>Administering our business including:</li>

        <ul>
          <li> Motor insurance policy pricing and underwriting</li>
          <li>Fraud prevention and detection</li>
          <li>Debt recovery</li>
          <li>Verifying your identity</li>
          <li>Carrying out customer satisfaction surveys</li>
          <li>Resolving complaints</li>
          <li>Researching new products</li>
          <li>Evaluating new insurers and suppliers</li>
        </ul>
        <li>
          To enable us by way of analysis to improve and develop the services we
          provide;
        </li>
        <li>
          Using automated means, to decide what products and services you may be
          more likely to be interested in or how we administer products and the
          service we provide to you;
        </li>
        <li>
          Updating you on promotions that you have participated in where we may
          contact you to remind you of the benefits you could receive;
        </li>
        <li>
          Letting your vehicle manufacturer and/or your franchise dealers know
          the take up of the products that they promote and to share with them
          details of complaints and other feedback you might provide about our
          services;
        </li>
        <li>
          To contact you to let you know about significant updates to this Fair
          Use Notice (you can check back here anytime to see the current
          version).
        </li>
      </ul>
      <p>
        Additionally, should your vehicle manufacturer appoint another company
        to provide their branded motor insurance in our place, and where you
        have an active branded motor insurance policy then we will provide your
        data to that company solely for the purpose of them providing you with a
        branded motor Insurance renewal quote.
      </p>
      <p>
        You can ask us not to process the data in any of these ways and we will
        consider your request fairly (see your rights to object to processing of
        your data below)
      </p>
      <h4>Additional Data Permissions</h4>
      <p>
        If you have not asked us for a quote or received a product or service
        from us we will only promote additional products and services to you
        where you have given us your consent. For instance, if you give us your
        renewal date we will consider that consent to send you a quote near the
        time of your renewal. You can update the consents you have given us{' '}
        <Link
          className='in-text'
          id='lnkUpdatePreference'
          to={PageAbsolutePath.Insurance.UpdatePreferencesPage}>
          here
        </Link>{' '}
        or by calling us on{' '}
        <a className='calltext' href={`tel:${phoneNumber}`}>
          {phoneNumber}
        </a>
        .
      </p>
      <p>
        When you report a claim to us or use our Accident Aftercare service we
        will share certain data with your franchise dealer, but we will only do
        that if you give us permission at the time you report the incident to
        us.
      </p>
      <h4>Who else will we provide your data to?</h4>
      <p>
        We, or the insurers that we deal with, may provide your data to certain
        third parties in relation to the services that you request from us or
        that are covered by an insurance contract or promotion you have
        participated in.
      </p>
      <p>These include</p>
      <ul>
        <li>your vehicle manufacturer and your franchise dealer;</li>
        <li>insurers or product providers;</li>
        <li>our approved suppliers including</li>
        <ul>
          <li>Claim administrators, loss adjusters, recovery agents</li>
          <li>approved repairers and network managers</li>
          <li>premium finance providers</li>
          <li>
            companies providing customer satisfaction surveys and ecommerce
            assistance
          </li>
          <li>
            marketing companies that assist us marketing our products and
            services
          </li>
          <li>IT providers</li>
        </ul>
        <li>providers of Regulatory Compliance Services;</li>
        <li>the Motor Insurance Database (MID);</li>
        <li>the Claims and Underwriting Exchange (CUE);</li>
        <li>the Motor Insurance Anti-Fraud and Theft Register (MIAFTR);</li>
        <li>
          Lexis-Nexis NCD Database, a database that allows us to verify your No
          Claims Discount;
        </li>
        <li>emergency services, police, medical and legal practices; and</li>
        <li>our professional advisers</li>
      </ul>
      <h4>Transfer of data outside of the UK and the EU.</h4>
      <p>
        We use servers located in the UK to store your personal information or
        elsewhere in the EU where it is protected by laws equivalent to those in
        the UK.
      </p>
      <p>
        Some of our suppliers and insurers from whom we source insurance quotes
        may transfer your personal information outside the EU, where this is the
        case our contracts with such suppliers require them to provide
        equivalent levels of protection for your personal information as it
        would have anywhere in the EU.
      </p>
      <h4>How long will we keep your data?</h4>
      <p>
        Where you do not enter into an insurance contract with us we will delete
        or anonymise your data during the third year after you last provided it
        to us or updated it unless there is a reason to hold it for longer, such
        as you made a complaint that was not resolved quickly.
      </p>
      <p>
        Where you enter into an insurance contract, including a Driveaway
        Insurance, arranged by us we will need to hold your data for the period
        that a third party is entitled to notify a claim under that policy which
        can be brought many years after the event, however we will pseudo
        anonymise your data in the third year after the expiry of your policy.
        That means it will be held in our systems in such a way that your data
        cannot be identified except by reference to a separately held and
        secured data file.
      </p>
      <p>
        If we have been provided with your data by a third party in order to
        contact you to provide you with a quote for a product or service and we
        are not able to contact you or, when we do, we not provide you with a
        quote, we will delete your data within 12 months of receiving it, unless
        you have opted in by another means.
      </p>
      <h4>Your rights in respect of your personal data</h4>
      <p>
        You as someone whose personal data we process have certain rights as to
        how we process it; namely:
      </p>
      <p>
        <u>The right to be informed</u>
      </p>
      <p>
        You are entitled to be informed in a transparent manner about the
        processing of your data and that is what we have set out in this Fair
        Use Notice. If you have any questions about how your data is processed
        then please contact us on
        <a className='calltext' href={`tel:${phoneNumber}`}>
          {phoneNumber}
        </a>
        and we will try to answer your questions.
      </p>
      <p>
        <u>The right of access</u>
      </p>
      <p>
        You are entitled to know what data we are processing and to receive a
        copy of it. You can request this from us at any time. This is known as a
        Subject Access Request and in normal circumstances we cannot charge you
        for providing you with a copy of your personal data that we process. We
        will normally provide you with a copy of your data within one month of
        receiving your request.
      </p>
      <p>
        <u>The right to rectification</u>
      </p>
      <p>
        You are entitled to have corrected any incorrect or incomplete
        information that we hold about you.
      </p>
      <p>
        <u>The right to be forgotten</u>
      </p>
      <p>
        You are entitled to request that we delete your personal data where we
        no longer have a legal basis for processing it. We will delete your data
        if you request that we do so and if:
      </p>
      <ul>
        <li>It was no longer necessary for the purpose you gave it to us,</li>
        <li>
          We were processing it based solely on your consent and you have
          withdrawn your consent; and
        </li>
        <li>
          There is no overriding legitimate interest for us to continue to
          process it.
        </li>
      </ul>
      <p>
        <u>The right to restrict processing</u>
      </p>
      <p>
        You are entitled to request that we restrict the processing of data so
        that we only store your data and not process it further and we shall
        comply where one of the following applies:
      </p>
      <ul>
        <li>
          the accuracy of the personal data is contested by you, for a period
          enabling us to verify and or rectify the accuracy of the personal
          data;
        </li>
        <li>
          we have processed the data without a lawful basis and you do not wish
          us to delete it and have asked that we restrict its use instead;
        </li>
        <li>
          we no longer need the personal data for the purpose originally
          intended, but you want us to retain it in relation to a legal claim;
        </li>
        <li>
          you have raised an objection to the processing of the data and we are
          in the process of considering whether to agree to that objection.
        </li>
      </ul>
      <p>
        <u>The right to object</u>
      </p>
      <p>
        You are entitled to object to our processing of your personal data for
        our legitimate interests beyond that which is strictly necessary in
        relation to a contract you have entered in to. If you make such a
        request we will either agree to your request or let you know any reason
        we have for not agreeing to it.
      </p>
      <p>
        Where we have passed your data to another party and you make a request
        to exercise your rights in relation to that data we will endeavour to
        pass on your request to all such third parties who will be similarly
        obliged to treat your request in accordance with your rights as a data
        subject.
      </p>
      <h4>
        Your right to lodge a complaint with the Information Commissioner.
      </h4>
      <p>
        You are entitled to make a complaint to the Information Commissioners
        Office which you can do:
      </p>
      <ul>
        <li>
          via their website{' '}
          <a className='in-text' href='https://ico.org.uk/' target='_blank'>
            https://ico.org.uk
          </a>
        </li>
        <li>by calling 0303 123 1113 or 01625 545 745; or</li>
        <li>by writing to</li>
      </ul>
      <p>Information Commissioner's Office</p>
      <p>
        Wycliffe House
        <br />
        Water Lane
        <br />
        Wilmslow
        <br />
        Cheshire
        <br />
        SK9 5AF
        <br />
      </p>
      <p>This Fair Use Notice was last updated on 18th September 2024 – V13</p>

      {!isShowingInPopup && (
        <div className='flex-row'>
          <ButtonBack
            name='btnBack'
            text='Back'
            onClick={goBack}
            additionalClassNames='btn-back'
          />
        </div>
      )}
    </div>
  );
};

export default FairUseNoticePage;
